import { useMemo, useState } from "react"

import {
  useGetEnterpriseDashboardAlertChartsQuery,
  useGetEnterpriseDashboardOrderChartsQuery,
  useGetEnterpriseDashboardOrderStatusesQuery,
  useGetEnterpriseDashboardPerftestChartsQuery,
  useGetEnterpriseDashboardThermostatChartsQuery,
  useGetEnterpriseDashboardThermostatTrendQuery,
  useGetEnterpriseDashboardTotalStatsQuery,
} from "store/api/widgetsApi"

import { useContractors } from "hooks/useContractors"
import { useUserProfile } from "hooks/useUserProfile"

import { chartColors, chartTextColor } from "./chartConfig"
import type { LegendItem, SelectOption } from "./components"
import {
  buildBarChartData,
  buildCustomerTrendColumns,
  buildDoughnutData,
  buildLineChartData,
  buildPassRateSeries,
  buildSeriesTrendColumn,
  buildTrendColumn,
  dashboardDateRangeOptions,
  dayPeriodFromOption,
  emptyAlertCharts,
  emptyEnterpriseTotalStats,
  emptyOrderCharts,
  emptyOrderStatuses,
  emptyPerftestCharts,
  emptyThermostatCharts,
  emptyThermostatTrend,
  getPercent,
  getSafeTimeSeriesLabels,
  getSeriesCounts,
  getSeriesTotal,
  periodFromOption,
  trendsDateRangeOptions,
} from "./dashboardData"

export const useEnterpriseDashboardData = () => {
  const { enterpriseContractorIds } = useUserProfile()
  const { contractors, isLoading: isContractorsLoading } = useContractors(enterpriseContractorIds)
  const [thermostatTrendOption, setThermostatTrendOption] = useState(trendsDateRangeOptions[0])
  const [customerTrendOption, setCustomerTrendOption] = useState(trendsDateRangeOptions[0])
  const [systemHealthTrendOption, setSystemHealthTrendOption] = useState(trendsDateRangeOptions[0])
  const [thermostatChartOption, setThermostatChartOption] = useState(dashboardDateRangeOptions[0])
  const [orderChartOption, setOrderChartOption] = useState(dashboardDateRangeOptions[0])
  const [alertChartOption, setAlertChartOption] = useState(dashboardDateRangeOptions[0])
  const [perftestChartOption, setPerftestChartOption] = useState(dashboardDateRangeOptions[0])
  const [trendsContractorId, setTrendsContractorId] = useState("")
  const [thermostatsContractorId, setThermostatsContractorId] = useState("")
  const [ordersContractorId, setOrdersContractorId] = useState("")
  const [alertsContractorId, setAlertsContractorId] = useState("")
  const [performanceTestsContractorId, setPerformanceTestsContractorId] = useState("")

  const thermostatTrendPeriod = dayPeriodFromOption(thermostatTrendOption)
  const customerTrendPeriod = dayPeriodFromOption(customerTrendOption)
  const systemHealthTrendPeriod = dayPeriodFromOption(systemHealthTrendOption)
  const thermostatChartPeriod = periodFromOption(thermostatChartOption)
  const orderChartPeriod = periodFromOption(orderChartOption)
  const alertChartPeriod = periodFromOption(alertChartOption)
  const perftestChartPeriod = periodFromOption(perftestChartOption)
  const trendsContractorQuery = trendsContractorId ? Number(trendsContractorId) : undefined
  const thermostatsContractorQuery = thermostatsContractorId ? Number(thermostatsContractorId) : undefined
  const ordersContractorQuery = ordersContractorId ? Number(ordersContractorId) : undefined
  const alertsContractorQuery = alertsContractorId ? Number(alertsContractorId) : undefined
  const performanceTestsContractorQuery = performanceTestsContractorId
    ? Number(performanceTestsContractorId)
    : undefined

  const { data: totalStatsResponse, isFetching: isTotalStatsFetching } = useGetEnterpriseDashboardTotalStatsQuery()
  const { data: thermostatTrendResponse } = useGetEnterpriseDashboardThermostatTrendQuery({
    period: thermostatTrendPeriod,
    contractorId: trendsContractorQuery,
  })
  const { data: customerTrendChartsResponse } = useGetEnterpriseDashboardThermostatChartsQuery({
    period: customerTrendPeriod,
    contractorId: trendsContractorQuery,
  })
  const { data: systemHealthAlertChartsResponse } = useGetEnterpriseDashboardAlertChartsQuery({
    period: systemHealthTrendPeriod,
    contractorId: trendsContractorQuery,
  })
  const { data: systemHealthPerftestChartsResponse } = useGetEnterpriseDashboardPerftestChartsQuery({
    period: systemHealthTrendPeriod,
    contractorId: trendsContractorQuery,
  })
  const { data: thermostatChartsResponse } = useGetEnterpriseDashboardThermostatChartsQuery({
    period: thermostatChartPeriod,
    contractorId: thermostatsContractorQuery,
  })
  const { data: orderStatusesResponse } = useGetEnterpriseDashboardOrderStatusesQuery({
    contractorId: ordersContractorQuery,
  })
  const { data: orderChartsResponse } = useGetEnterpriseDashboardOrderChartsQuery({
    period: orderChartPeriod,
    contractorId: ordersContractorQuery,
  })
  const { data: alertChartsResponse } = useGetEnterpriseDashboardAlertChartsQuery({
    period: alertChartPeriod,
    contractorId: alertsContractorQuery,
  })
  const { data: perftestChartsResponse } = useGetEnterpriseDashboardPerftestChartsQuery({
    period: perftestChartPeriod,
    contractorId: performanceTestsContractorQuery,
  })

  const totalStats = totalStatsResponse?.data ?? emptyEnterpriseTotalStats
  const thermostatTrend = thermostatTrendResponse?.data ?? emptyThermostatTrend
  const customerTrendCharts = customerTrendChartsResponse?.data ?? emptyThermostatCharts
  const systemHealthAlertCharts = systemHealthAlertChartsResponse?.data ?? emptyAlertCharts
  const systemHealthPerftestCharts = systemHealthPerftestChartsResponse?.data ?? emptyPerftestCharts
  const thermostatCharts = thermostatChartsResponse?.data ?? emptyThermostatCharts
  const orderStatuses = orderStatusesResponse?.data ?? emptyOrderStatuses
  const orderCharts = orderChartsResponse?.data ?? emptyOrderCharts
  const alertCharts = alertChartsResponse?.data ?? emptyAlertCharts
  const perftestCharts = perftestChartsResponse?.data ?? emptyPerftestCharts

  const contractorOptions: SelectOption[] = useMemo(
    () => [
      { label: "All Contractors", value: "" },
      ...contractors
        .filter(contractor => contractor.id && contractor.name)
        .map(contractor => ({ label: contractor.name!, value: String(contractor.id) })),
    ],
    [contractors],
  )

  const installationTrendColumns = useMemo(
    () => [
      buildTrendColumn("Installed Thermostats", thermostatTrend.instaled_devices, thermostatTrendPeriod),
      buildTrendColumn("Ordered Thermostats", thermostatTrend.ordered_devices, thermostatTrendPeriod),
    ],
    [thermostatTrend, thermostatTrendPeriod],
  )

  const customerTrendColumns = useMemo(() => {
    return buildCustomerTrendColumns(customerTrendCharts.mobile_app_vs_customers, customerTrendPeriod)
  }, [customerTrendCharts, customerTrendPeriod])

  const systemHealthTrendColumns = useMemo(() => {
    const activeAlerts = systemHealthAlertCharts.alerts_statuses.active
    const pass = systemHealthPerftestCharts.pass
    const issue = systemHealthPerftestCharts.issue
    const activeAlertSeries = getSeriesCounts(getSafeTimeSeriesLabels([activeAlerts]), activeAlerts)
    const passRateSeries = buildPassRateSeries(pass, issue)
    const passTotal = getSeriesTotal(pass)
    const issueTotal = getSeriesTotal(issue)
    const testTotal = passTotal + issueTotal
    const passPercent = getPercent(passTotal, testTotal)
    const issuePercent = getPercent(issueTotal, testTotal)

    return [
      buildSeriesTrendColumn({
        label: "Active Alerts",
        period: systemHealthTrendPeriod,
        series: activeAlertSeries,
      }),
      buildSeriesTrendColumn({
        label: "Passed Tests",
        period: systemHealthTrendPeriod,
        series: passRateSeries,
        subValue: `% (Issues: ${issuePercent}%)`,
        value: String(passPercent),
        hideChart: true,
      }),
    ]
  }, [systemHealthAlertCharts, systemHealthPerftestCharts, systemHealthTrendPeriod])

  const installsOverTimeData = useMemo(
    () =>
      buildLineChartData([
        {
          key: "installed",
          label: "Installed",
          points: thermostatCharts.installs_over_time.series,
          color: "#22c55e",
          fillColor: "rgba(34, 197, 94, 0.42)",
          gradientEndColor: "rgba(34, 197, 94, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
      ]),
    [thermostatCharts],
  )

  const installedVsOrderedData = useMemo(
    () =>
      buildLineChartData([
        {
          key: "installed",
          label: "Installed",
          points: thermostatCharts.installed_vs_ordered_trend.installed,
          color: "#10b981",
          fill: false,
          borderWidth: 2.5,
          tension: 0,
          pointRadius: 4,
          pointHoverRadius: 4,
          pointBorderWidth: 2,
        },
        {
          key: "ordered",
          label: "Ordered",
          points: thermostatCharts.installed_vs_ordered_trend.ordered,
          color: "#3b82f6",
          fill: false,
          borderWidth: 2.5,
          tension: 0,
          pointRadius: 4,
          pointHoverRadius: 4,
          pointBorderWidth: 2,
        },
      ]),
    [thermostatCharts],
  )

  const mobileAppVsCustomersData = useMemo(
    () =>
      buildBarChartData([
        {
          key: "customers",
          label: "Customers",
          points: thermostatCharts.mobile_app_vs_customers.customers,
          color: chartTextColor,
        },
        {
          key: "mobile_app",
          label: "Mobile App",
          points: thermostatCharts.mobile_app_vs_customers.mobile_app,
          color: chartColors.mobileApp,
        },
      ]),
    [thermostatCharts],
  )

  const orderStatusChartData = useMemo(() => buildDoughnutData(orderStatuses), [orderStatuses])

  const orderStatusLegendItems: LegendItem[] = [
    { label: "New", value: orderStatuses.new, color: chartColors.new },
    { label: "Fulfilled", value: orderStatuses.fulfilled, color: chartColors.fulfilled },
    { label: "Confirmed", value: orderStatuses.confirmed, color: chartColors.confirmed },
    { label: "Denied", value: orderStatuses.denied, color: chartColors.denied },
    { label: "Packed", value: orderStatuses.packed, color: chartColors.packed },
    { label: "Shipping", value: orderStatuses.shipping, color: chartColors.shipping },
  ]

  const orderedByTypeChartData = useMemo(
    () =>
      buildBarChartData([
        {
          key: "thermostats",
          label: "Nuve Thermostats",
          points: orderCharts.thermostats,
          color: chartColors.thermostats,
        },
        { key: "cwa", label: "Wire Adapters (CWA)", points: orderCharts.cwa, color: chartColors.cwa },
        { key: "ths", label: "THS", points: orderCharts.ths, color: chartColors.ths },
        { key: "wls", label: "WLS", points: orderCharts.wls, color: chartColors.wls },
      ]),
    [orderCharts],
  )

  const alertsOverTimeChartData = useMemo(
    () =>
      buildLineChartData([
        {
          key: "efficiency_issue",
          label: "System Efficiency",
          points: alertCharts.alerts_over_time.efficiency_issue,
          color: "#16a34a",
          fillColor: "rgba(22, 163, 74, 0.34)",
          gradientEndColor: "rgba(22, 163, 74, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
        {
          key: "air_quality",
          label: "Air Quality",
          points: alertCharts.alerts_over_time.air_quality,
          color: "#d97706",
          fillColor: "rgba(217, 119, 6, 0.3)",
          gradientEndColor: "rgba(217, 119, 6, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
        {
          key: "fan_malfunction",
          label: "Fan Malfunction",
          points: alertCharts.alerts_over_time.fan_malfunction,
          color: "#dc2626",
          fillColor: "rgba(220, 38, 38, 0.3)",
          gradientEndColor: "rgba(220, 38, 38, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
        {
          key: "ambient_sensor_malfunction",
          label: "Ambient Sensor Malfunction",
          points: alertCharts.alerts_over_time.ambient_sensor_malfunction,
          color: "#2563eb",
          fillColor: "rgba(37, 99, 235, 0.3)",
          gradientEndColor: "rgba(37, 99, 235, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
        {
          key: "humidity_sensor_malfunction",
          label: "Humidity Sensor Malfunction",
          points: alertCharts.alerts_over_time.humidity_sensor_malfunction,
          color: "#4f46e5",
          fillColor: "rgba(79, 70, 229, 0.3)",
          gradientEndColor: "rgba(79, 70, 229, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
        {
          key: "temperature_sensor_malfunction",
          label: "Temperature Sensor Malfunction",
          points: alertCharts.alerts_over_time.temperature_sensor_malfunction,
          color: "#7c3aed",
          fillColor: "rgba(124, 58, 237, 0.3)",
          gradientEndColor: "rgba(124, 58, 237, 0.08)",
          borderWidth: 2.5,
          tension: 0,
        },
      ]),
    [alertCharts],
  )

  const alertStatusChartData = useMemo(
    () =>
      buildBarChartData([
        { key: "active", label: "Active", points: alertCharts.alerts_statuses.active, color: chartColors.active },
        { key: "frozen", label: "Frozen", points: alertCharts.alerts_statuses.frozen, color: chartColors.frozen },
        {
          key: "acknowledged",
          label: "Acknowledged",
          points: alertCharts.alerts_statuses.acknowledged,
          color: chartColors.acknowledged,
        },
      ]),
    [alertCharts],
  )

  const performanceChartData = useMemo(
    () =>
      buildBarChartData([
        { key: "pass", label: "Pass", points: perftestCharts.pass, color: chartColors.pass },
        { key: "issue", label: "Issue", points: perftestCharts.issue, color: chartColors.issue },
      ]),
    [perftestCharts],
  )

  return {
    contractorOptions,
    isContractorsLoading,
    totalStats,
    isTotalStatsFetching,
    trends: {
      thermostatTrendOption,
      customerTrendOption,
      systemHealthTrendOption,
      contractorId: trendsContractorId,
      installationTrendColumns,
      customerTrendColumns,
      systemHealthTrendColumns,
      setThermostatTrendOption,
      setCustomerTrendOption,
      setSystemHealthTrendOption,
      setContractorId: setTrendsContractorId,
    },
    thermostats: {
      thermostatChartOption,
      contractorId: thermostatsContractorId,
      installsOverTimeData,
      installedVsOrderedData,
      mobileAppVsCustomersData,
      setThermostatChartOption,
      setContractorId: setThermostatsContractorId,
    },
    orders: {
      orderChartOption,
      contractorId: ordersContractorId,
      orderStatuses,
      orderStatusChartData,
      orderStatusLegendItems,
      orderedByTypeChartData,
      setOrderChartOption,
      setContractorId: setOrdersContractorId,
    },
    alerts: {
      alertChartOption,
      contractorId: alertsContractorId,
      alertsOverTimeChartData,
      alertStatusChartData,
      setAlertChartOption,
      setContractorId: setAlertsContractorId,
    },
    performanceTests: {
      perftestChartOption,
      contractorId: performanceTestsContractorId,
      performanceChartData,
      setPerftestChartOption,
      setContractorId: setPerformanceTestsContractorId,
    },
  }
}
